import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, ButtonGroup, ButtonGroupItem, Dropdown, Form, Input, Legend, RadioGroup, Radio } from '@boozallen-uip/uswds-react-core';
import { useNavigate } from 'react-router-dom';
import { FormField, FormFieldBody, FormFieldContainer, FormSectionContainer, FormSectionHeader, FormSectionHeaderContainer, FormSectionSubHeader, PageSubHeader, SectionContainer } from '../shared.styled';
import { UserContext } from '../UserContext';
import { BASE_TITLE, VENDOR_DASHBOARD_URL, stateLabelValues, ENTITY_TYPE_VENDOR, ENTITY_TYPE_AGENCY } from '../../utlities/core.constants';
import useEntity from '../../hooks/useEntity.hook';
import usePrimaryPoc from '../../hooks/usePrimaryPoc.hook';
import PageHeader from '../../components/PageHeader/PageHeader.component';
import { validateZipCode } from '../../utlities/common.funcs';

function FormFieldRequired() {
  return <span className='text-red'>(Required)</span>
}

export default function OrganizationCreate() {
  const [entityType, setEntityType] = useState(ENTITY_TYPE_VENDOR);
  const [organization, setOrganization] = useState({
    name: '',
    dba: '',
    description: '',
    isActive: true
  });
  const [primaryPoc, setPrimaryPoc] = useState({
    phoneNumber: '',
    email: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [zipError, setZipError] = useState(null);
  const errorRef = useRef(null);
  const { isSuperUser } = useContext(UserContext);
  const { createVendor, createAgency } = useEntity();
  const { submitPrimaryPoc } = usePrimaryPoc();

  const navigate = useNavigate();

  useEffect(() => {
    if (submitError && errorRef.current) {
      errorRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [submitError]);

  document.title = `${BASE_TITLE} - Create Organization`;

  // Redirect if not super user
  if (!isSuperUser) {
    navigate(VENDOR_DASHBOARD_URL);
    return null;
  }

  const updateOrganization = (patch) => {
    setOrganization({ ...organization, ...patch });
  };

  const onSubmitHandler = useCallback(async () => {
    try {
      setIsSaving(true);
      setSubmitError(null);
      setZipError(null);

      const missingFields = [];
      if (!organization.name) {
        missingFields.push(entityType === ENTITY_TYPE_VENDOR ? 'company name' : 'agency name');
      }
      if (!primaryPoc.phoneNumber) missingFields.push('phone number');
      if (!primaryPoc.email) missingFields.push('email address');
      if (missingFields.length) {
        const fieldList = missingFields.length === 1
          ? missingFields[0]
          : `${missingFields.slice(0, -1).join(', ')}${missingFields.length > 2 ? ',' : ''} and ${missingFields[missingFields.length - 1]}`;
        setSubmitError({
          heading: 'Missing Required Information',
          message: `Enter your ${fieldList}.`,
        });
        return;
      }

      const zipValidationMessage = validateZipCode(primaryPoc.zipCode);
      if (zipValidationMessage) {
        setZipError(zipValidationMessage);
        return;
      }

      // Create Primary POC first
      const primaryPocResponse = await submitPrimaryPoc({
        name: organization.name,
        phoneNumber: primaryPoc.phoneNumber,
        email: primaryPoc.email,
        street1: primaryPoc.street1,
        street2: primaryPoc.street2,
        city: primaryPoc.city,
        state: primaryPoc.state,
        zipCode: String(primaryPoc.zipCode ?? '').trim(),
        isActive: true
      });

      if (!primaryPocResponse || !primaryPocResponse.data || !primaryPocResponse.data.id) {
        setSubmitError({
          heading: 'Primary Contact Not Saved',
          message: 'Failed to create primary contact. Please confirm all fields have been completed and try again.',
        });
        return;
      }

      // Create organization with POC ID
      const organizationData = {
        name: organization.name,
        description: organization.description,
        primaryPOCName: organization.name,
        primaryPOCEmail: primaryPoc.email,
        primaryPocId: primaryPocResponse.data.id,
        isActive: organization.isActive
      };

      // Add DBA for vendors only
      if (entityType === ENTITY_TYPE_VENDOR) {
        organizationData.dba = organization.dba;
      }

      let response;
      if (entityType === ENTITY_TYPE_VENDOR) {
        response = await createVendor(organizationData);
      } else {
        response = await createAgency(organizationData);
      }

      if (response && response.data && response.data.id) {
        const entityName = entityType === ENTITY_TYPE_VENDOR ? 'Vendor' : 'Agency';

        // Redirect to vendor detail page for vendors, dashboard for agencies
        if (entityType === ENTITY_TYPE_VENDOR) {
          navigate(`/vendor/${response.data.id}`, {
            state: {
              success: true,
              message: `${entityName} "${organization.name}" was created successfully.`
            }
          });
        } else {
          navigate(VENDOR_DASHBOARD_URL, {
            state: {
              success: true,
              message: `${entityName} "${organization.name}" was created successfully.`
            }
          });
        }
      } else {
        setSubmitError({
          heading: 'Organization Not Saved',
          message: 'We couldn\'t save the organization right now. Please try again.',
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      // Check for duplicate name error (409 Conflict)
      if (e.response && e.response.status === 409) {
        setSubmitError({
          heading: 'Duplicate Organization',
          message: 'An organization with this name already exists. Please choose a different name (or review existing records to locate and contact the administrator).',
        });
      } else {
        setSubmitError({
          heading: 'Organization Not Saved',
          message: 'An error occurred while creating the organization. Please try again.',
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [organization, primaryPoc, entityType, submitPrimaryPoc, createVendor, createAgency, navigate]);

  return (
    <div>
      <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize='2800px 391px' className='2xl:!bg-cover'>
        Create an Organization
      </PageHeader>
      <SectionContainer className='pt-4'>
        <div className='text-primary-darker'>
          <a className="text-primary-darker" href="/">Home &gt; Repository &gt; </a> Create Organization
        </div>
        <PageSubHeader>Create a New Organization</PageSubHeader>
        <p className='mb-8rem'>
          Please fill out the form below to create a new organization. The information you provide will appear on the organization&apos;s overview page. It is important to include as much detail as possible.
        </p>

        {submitError && (
          <div ref={errorRef} className="mb-4" role="alert" aria-live="assertive">
            <Alert
              type="error"
              heading={submitError.heading}
              onClose={() => setSubmitError(null)}
            >
              {submitError.message}
            </Alert>
          </div>
        )}

        <Form id="organizationForm" onSubmit={onSubmitHandler} className='w-full max-w-full'>
          <FormSectionContainer>
            <FormSectionHeaderContainer>
              <FormSectionHeader>Organization Type</FormSectionHeader>
              <FormSectionSubHeader className='mb-0'>
                <p>Select whether you are creating a Vendor or Agency organization.</p>
              </FormSectionSubHeader>
            </FormSectionHeaderContainer>
            <FormFieldContainer>
              <FormFieldBody>
                <FormField className='col-start-1'>
                  <RadioGroup
                    id="entityType"
                    name="entityType"
                    value={entityType}
                    onChange={(e) => setEntityType(e.detail.value)}
                  >
                    <Legend className='mb-2'>Organization Type</Legend>
                    <Radio value={ENTITY_TYPE_VENDOR}>Vendor</Radio>
                    <Radio value={ENTITY_TYPE_AGENCY}>Agency</Radio>
                  </RadioGroup>
                </FormField>
              </FormFieldBody>
            </FormFieldContainer>
          </FormSectionContainer>

          <FormSectionContainer>
            <FormSectionHeaderContainer>
              <FormSectionHeader>Organization Information</FormSectionHeader>
              <FormSectionSubHeader className='mb-0'>
                <p>Please provide the organization&apos;s information as you would like it to appear on its Overview Page.</p>
                {entityType === ENTITY_TYPE_VENDOR && (
                  <p className='mb-0'>
                    <span className='font-bold'>Note: </span>Including a &quot;doing business as&quot; (DBA) name will override the company name in the page&apos;s title.
                  </p>
                )}
              </FormSectionSubHeader>
            </FormSectionHeaderContainer>
            <FormFieldContainer>
              <FormFieldBody>
                <FormField>
                  <Input
                    value={organization.name}
                    onChange={(e) => updateOrganization({ name: e.target.value })}
                    required
                  >
                    {entityType === ENTITY_TYPE_VENDOR ? 'Company' : 'Agency'} name <FormFieldRequired />
                  </Input>
                </FormField>
                {entityType === ENTITY_TYPE_VENDOR && (
                  <FormField>
                    <Input
                      value={organization.dba}
                      onChange={(e) => updateOrganization({ dba: e.target.value })}
                    >
                      DBA, if applicable
                    </Input>
                  </FormField>
                )}
                <FormField className='col-start-1'>
                  <Input
                    value={organization.description}
                    type="textarea"
                    onChange={(e) => updateOrganization({ description: e.target.value })}
                  >
                    {entityType === ENTITY_TYPE_VENDOR ? 'Company' : 'Agency'} description
                  </Input>
                </FormField>
              </FormFieldBody>
            </FormFieldContainer>
          </FormSectionContainer>

          <FormSectionContainer className='col-span-8'>
            <FormSectionHeaderContainer>
              <FormSectionHeader>Organization Contact Information</FormSectionHeader>
              <FormSectionSubHeader>
                Provide general contact information for the organization&apos;s accessibility department or personnel.
              </FormSectionSubHeader>
            </FormSectionHeaderContainer>
            <FormFieldContainer>
              <FormFieldBody>
                <FormField className='col-start-1'>
                  <Input
                    value={primaryPoc.phoneNumber}
                    onChange={(e) => setPrimaryPoc({ ...primaryPoc, phoneNumber: e.target.value })}
                    required
                  >
                    Phone number <FormFieldRequired />
                  </Input>
                </FormField>
                <FormField>
                  <Input
                    value={primaryPoc.email}
                    onChange={(e) => setPrimaryPoc({ ...primaryPoc, email: e.target.value })}
                    required
                  >
                    Email address <FormFieldRequired />
                  </Input>
                </FormField>

                <FormField className='col-start-1'>
                  <Input
                    value={primaryPoc.street1}
                    onChange={(e) => setPrimaryPoc({ ...primaryPoc, street1: e.target.value })}
                  >
                    Street Address Line 1
                  </Input>
                </FormField>
                <FormField>
                  <Input
                    value={primaryPoc.street2}
                    onChange={(e) => setPrimaryPoc({ ...primaryPoc, street2: e.target.value })}
                  >
                    Street Address Line 2
                  </Input>
                </FormField>

                <FormField className='col-start-1'>
                  <Input
                    value={primaryPoc.city}
                    onChange={(e) => setPrimaryPoc({ ...primaryPoc, city: e.target.value })}
                  >
                    City
                  </Input>
                </FormField>
                <FormField>
                  <Dropdown
                    id="state"
                    optionRenderer={(o) => o.label}
                    options={stateLabelValues}
                    value={primaryPoc.state}
                    comparator={(value, option) => value === option.value}
                    onChange={e => setPrimaryPoc({ ...primaryPoc, state: e.detail.value.value })}
                  >
                    State
                  </Dropdown>
                </FormField>
                <FormField className='col-start-1'>
                  <Input
                    value={primaryPoc.zipCode}
                    onChange={(e) => { setZipError(null); setPrimaryPoc({ ...primaryPoc, zipCode: e.target.value }); }}
                  >
                    ZIP Code
                  </Input>
                  {zipError && <div className="mt-1 text-sm text-red" role="alert">{zipError}</div>}
                </FormField>
              </FormFieldBody>
            </FormFieldContainer>
          </FormSectionContainer>

          <section>
            <ButtonGroup>
              <ButtonGroupItem>
                <Button outline onClick={() => navigate(VENDOR_DASHBOARD_URL)}>Cancel</Button>
              </ButtonGroupItem>
              <ButtonGroupItem>
                <Button
                  disabled={isSaving}
                  onClick={() => { setIsSaving(true); onSubmitHandler(); }}
                >
                  {isSaving ? 'Creating...' : 'Create Organization'}
                </Button>
              </ButtonGroupItem>
            </ButtonGroup>
          </section>
        </Form>
      </SectionContainer>
    </div>
  );
}

